import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Download, CheckCircle2, Smartphone, Apple, ArrowRight } from 'lucide-react';

import { useSiteSettings } from '../context/SiteSettingsContext';
import LanguageSwitcher from '../components/LanguageSwitcher';

/**
 * Public landing page reached when scanning the QR code or visiting /install.
 * - Detects already-installed PWA → redirects to /m
 * - Detects iOS Safari → shows the visual Add-to-Home-Screen guide
 * - Detects Android Chrome → uses the native beforeinstallprompt
 * - On desktop → shows the QR code so user can scan it from their phone
 */

function IOSShareIcon({ size = 20 }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 15V3" />
      <path d="m8 7 4-4 4 4" />
      <path d="M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7" />
    </svg>
  );
}

export default function PWAInstallPage() {
  const { t } = useTranslation();
  const { logo } = useSiteSettings();
  const navigate = useNavigate();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [platform, setPlatform] = useState('unknown'); // 'ios' | 'android' | 'desktop'

  useEffect(() => {
    const ua = navigator.userAgent || '';
    const iOS = /iPhone|iPad|iPod/i.test(ua);
    const android = /Android/i.test(ua);
    const isMobile = iOS || android;
    setPlatform(iOS ? 'ios' : android ? 'android' : isMobile ? 'mobile' : 'desktop');

    const standalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;
    if (standalone) {
      setInstalled(true);
      // Already installed: jump straight to the hub.
      const timer = setTimeout(() => navigate('/m', { replace: true }), 600);
      return () => clearTimeout(timer);
    }

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => setInstalled(true);
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, [navigate]);

  const triggerInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try {
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') setInstalled(true);
    } catch {}
    setDeferredPrompt(null);
  };

  return (
    <div
      className="min-h-screen bg-[#1A3C34] text-white"
      data-testid="install-page"
      style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <header className="px-5 pt-5 pb-3 flex items-center justify-between">
        <img src={logo} alt="Flexi" className="h-7 w-auto rounded bg-white/5 p-1" />
        <LanguageSwitcher />
      </header>

      <main className="px-5 pb-10 max-w-md mx-auto">
        {/* Hero */}
        <div className="text-center mt-6 mb-8">
          <div className="w-20 h-20 mx-auto rounded-3xl bg-white shadow-xl shadow-black/30 flex items-center justify-center mb-4">
            <img src="/icon-192.png" alt="Flexi" className="w-16 h-16 rounded-2xl" />
          </div>
          <h1 className="font-heading text-2xl font-bold leading-tight">
            {t('install.title', 'Installez Flexi')}
          </h1>
          <p className="text-sm text-white/60 mt-2 leading-relaxed">
            {t('install.tagline', 'L\'app pour réserver vos espaces et ouvrir la porte en un tap.')}
          </p>
        </div>

        {/* Already installed */}
        {installed && (
          <div data-testid="install-already" className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 text-center text-sm">
            <CheckCircle2 className="w-6 h-6 mx-auto text-green-400 mb-2" />
            <p className="font-semibold mb-1">{t('install.already', 'App déjà installée')}</p>
            <p className="text-xs text-white/60">{t('install.redirecting', 'Redirection…')}</p>
          </div>
        )}

        {/* Android: native prompt */}
        {!installed && platform === 'android' && (
          <div data-testid="install-android" className="space-y-4">
            {deferredPrompt ? (
              <button
                type="button"
                onClick={triggerInstall}
                data-testid="install-android-btn"
                className="w-full bg-[#C77D63] hover:bg-[#B86F58] active:scale-[0.98] transition-all rounded-2xl p-5 text-left flex items-center gap-4 shadow-lg shadow-black/30"
              >
                <Download size={24} className="flex-shrink-0" />
                <div className="flex-1">
                  <p className="font-heading font-bold text-lg leading-tight">{t('install.installNow', 'Installer maintenant')}</p>
                  <p className="text-xs text-white/80 mt-0.5">{t('install.installSub', 'Une icône sera ajoutée à votre écran d\'accueil')}</p>
                </div>
                <ArrowRight size={18} />
              </button>
            ) : (
              <div className="bg-white/5 border border-white/10 rounded-xl p-4 text-sm">
                <p className="font-semibold mb-2 inline-flex items-center gap-2">
                  <Smartphone size={16} className="text-[#C77D63]" />
                  {t('install.androidManual', 'Sur Android Chrome :')}
                </p>
                <ol className="space-y-2 text-white/70 text-xs ml-1">
                  <li>1. {t('install.androidStep1', 'Touchez les ⋮ en haut à droite')}</li>
                  <li>2. {t('install.androidStep2', 'Choisissez "Installer l\'application" ou "Ajouter à l\'écran d\'accueil"')}</li>
                  <li>3. {t('install.androidStep3', 'Confirmez en touchant Installer')}</li>
                </ol>
              </div>
            )}
          </div>
        )}

        {/* iOS: visual guide */}
        {!installed && platform === 'ios' && (
          <div data-testid="install-ios" className="bg-white/5 border border-white/10 rounded-xl p-5">
            <p className="font-semibold mb-1 inline-flex items-center gap-2">
              <Apple size={16} className="text-[#C77D63]" />
              {t('install.iosTitle', 'Sur iPhone (Safari uniquement)')}
            </p>
            <p className="text-[11px] text-[#C77D63] uppercase tracking-wider mb-4">
              {t('install.iosWarn', 'Cette page doit être ouverte dans Safari, pas Chrome iOS')}
            </p>
            <ol className="space-y-3 text-sm">
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-[#C77D63] text-white text-xs font-bold flex items-center justify-center flex-shrink-0">1</span>
                <span className="flex-1">
                  {t('install.iosStep1Pre', 'Touchez le bouton')}{' '}
                  <span className="inline-flex items-center gap-1 align-middle bg-white/10 px-2 py-0.5 rounded">
                    <strong>{t('install.iosShare', 'Partager')}</strong>
                    <IOSShareIcon size={14} />
                  </span>
                  {' '}{t('install.iosStep1Post', 'en bas de Safari')}
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-[#C77D63] text-white text-xs font-bold flex items-center justify-center flex-shrink-0">2</span>
                <span className="flex-1">
                  {t('install.iosStep2Pre', 'Faites défiler et touchez')}{' '}
                  <strong className="bg-white/10 px-2 py-0.5 rounded">+ {t('install.iosAddHome', 'Sur l\'écran d\'accueil')}</strong>
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-[#C77D63] text-white text-xs font-bold flex items-center justify-center flex-shrink-0">3</span>
                <span className="flex-1">
                  {t('install.iosStep3', 'Touchez "Ajouter" en haut à droite')}
                </span>
              </li>
            </ol>
            <p className="text-[11px] text-white/50 mt-4 leading-relaxed border-t border-white/10 pt-3">
              {t('install.iosNote', '⚠️ N\'utilisez pas "Signet" ou "Liste de lecture" — l\'app ne fonctionnera pas en mode hors-ligne.')}
            </p>
          </div>
        )}

        {/* Desktop / unsupported */}
        {!installed && (platform === 'desktop' || platform === 'unknown' || platform === 'mobile') && (
          <div data-testid="install-desktop" className="bg-white/5 border border-white/10 rounded-xl p-5 text-center">
            {deferredPrompt ? (
              <button
                type="button"
                onClick={triggerInstall}
                data-testid="install-desktop-btn"
                className="w-full bg-[#C77D63] hover:bg-[#B86F58] rounded-xl p-4 font-bold inline-flex items-center justify-center gap-2"
              >
                <Download size={18} />
                {t('install.installNow', 'Installer maintenant')}
              </button>
            ) : (
              <>
                <Smartphone size={32} className="mx-auto text-white/40 mb-2" />
                <p className="text-sm font-semibold mb-1">
                  {t('install.openOnPhone', 'Ouvrez cette page sur votre téléphone')}
                </p>
                <p className="text-xs text-white/60">
                  {t('install.openOnPhoneSub', 'L\'installation native fonctionne uniquement depuis un mobile.')}
                </p>
              </>
            )}
          </div>
        )}

        {/* Why install */}
        <div className="mt-8 bg-white/[0.03] rounded-xl p-4 text-xs text-white/60 leading-relaxed">
          <p className="font-semibold text-white/80 mb-2">{t('install.whyTitle', 'Pourquoi installer ?')}</p>
          <ul className="space-y-1.5">
            <li>✓ {t('install.why1', 'Codes d\'accès consultables hors ligne')}</li>
            <li>✓ {t('install.why2', 'Ouvrir la porte en 1 tap depuis l\'écran d\'accueil')}</li>
            <li>✓ {t('install.why3', 'Réservation rapide en 4 étapes')}</li>
            <li>✓ {t('install.why4', 'Pas de barre d\'adresse, plein écran')}</li>
          </ul>
        </div>

        <div className="text-center mt-6">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="text-xs text-white/40 hover:text-white/60 underline-offset-4 hover:underline"
          >
            {t('install.skip', 'Continuer sans installer')}
          </button>
        </div>
      </main>
    </div>
  );
}
